/**
 * Shared API types between backend and frontend
 */

import type { DrataUploadError } from '../utils/drataErrors'

// Report types
export const REPORT_TYPES = ['soc2_type1', 'soc2_type2', 'soc3', 'iso27001', 'iso27017'] as const
export type ReportType = (typeof REPORT_TYPES)[number]

export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  soc2_type1: 'SOC 2 Type I',
  soc2_type2: 'SOC 2 Type II',
  soc3: 'SOC 3',
  iso27001: 'ISO 27001',
  iso27017: 'ISO 27017',
}

// Review data types (extracted from compliance reports)

export interface Finding {
  title: string
  description: string
  severity?: 'low' | 'medium' | 'high' | 'critical'
  pageNumbers?: number[]
  note?: string
}

export interface UserEntityControl {
  description: string
  impact?: string | null
  pageNumbers?: number[]
}

export interface Subservice {
  name: string
  description: string
  pageNumbers?: number[]
}

export interface SOC2ReviewData {
  vendorName: string
  productName: string
  productDescription: string
  reportIssueDate: string
  cpaFirm: string
  auditorCredibility: string
  reportType: string
  trustCriteria: string[]
  auditPeriodStart: string
  auditPeriodEnd: string
  auditorOpinion: string
  auditorConclusion?: string
  userEntityControls?: UserEntityControl[]
  subservicesUsage?: Subservice[]
  findings?: Finding[]
  conclusionSummary: string
}

// Union of all review data types - extend as new report types are added
export type ReviewData = SOC2ReviewData

// Auth types

import type { UserRole, AssignableRole } from '../permissions'

// Re-export for convenience
export type { UserRole, AssignableRole }

/**
 * Base user type - used across auth and user management.
 * userId is our internal users table UUID.
 */
export interface User {
  userId: string
  email: string
  role: UserRole
}

// Explicit aliases for clarity in different contexts
export type AuthUser = User
export type AuthMeResponse = User

export interface LogoutResponse {
  message: string
}

// Reviews types

export const REVIEW_STATUSES = ['pending', 'extracting', 'ai_complete', 'complete', 'failed'] as const
export type ReviewStatus = (typeof REVIEW_STATUSES)[number]

// Validity status for compliance reports
export const VALIDITY_STATUSES = ['valid', 'caution', 'invalid', 'unknown'] as const
export type ValidityStatus = (typeof VALIDITY_STATUSES)[number]

export interface ValidityInfo {
  status: ValidityStatus
  daysRemaining: number
  expirationDate: Date | null
  message: string
}

export interface Review {
  id: number
  createdAt: string
  status: ReviewStatus
  reportType: string | null
  sourceUrl: string | null
  vendorId: string
  vendorName: string | null
  auditPeriodEnd: string | null
  archivedAt: string | null
  /** Active bridge letter attached to this review, if any (populated by the list endpoint). */
  bridgeLetter?: BridgeLetter | null
  /** True when no newer approved review exists for this vendor — gates the "Bridge Letter" label. */
  isLatestReview?: boolean
}

export interface ReviewsListResponse {
  reviews: Review[]
}

// Full review detail with extracted data (for detail page)
export interface ReviewDetail extends Review {
  reviewData: ReviewData | null
  extractionMetadata: ExtractionMetadata | null
  createdBy: string | null
  approvedBy: string | null
  approvedAt: string | null
  archivedBy: string | null
  auditorId: string | null
  auditorName: string | null
  auditorStatus: AuditorStatus | null
  auditorOpinion: string | null
  /** Active bridge letter attached to this review, if any. */
  bridgeLetter: BridgeLetter | null
  /**
   * True when no newer approved review exists for this vendor (auditPeriodEnd
   * DESC). Gates the "Bridge Letter until …" label per the bridge-letter model.
   */
  isLatestReview: boolean
}

export interface ExtractionMetadata {
  model: string
  inputTokens: number
  outputTokens: number
  durationMs: number
  pageCount: number
  errorDetails?: string
}

export interface ReviewDetailResponse {
  review: ReviewDetail
}

export interface UpdateReviewRequest {
  reviewData: ReviewData
  auditorId: string
}

export interface UpdateReviewResponse {
  success: boolean
  review: ReviewDetail
}

export interface ApproveReviewResponse {
  success: boolean
  review: ReviewDetail
}

export interface ArchiveReviewResponse {
  success: boolean
  review: ReviewDetail
}

// Edit history types
export interface DiffEntry {
  field: string
  old: unknown
  new: unknown
}

export interface ReviewEdit {
  id: string
  reviewId: number
  editorEmail: string
  editedAt: string
  previousData: Record<string, unknown>
  newData: Record<string, unknown>
  diff?: DiffEntry[] | null
}

export interface ReviewHistoryResponse {
  edits: ReviewEdit[]
}

// ============================================================================
// Bridge Letter Types
// ============================================================================

/**
 * A bridge letter attached to a single compliance review. Stored as a PDF in
 * object storage; `startDate`/`endDate` are the useful-window bounds (both
 * `YYYY-MM-DD`, matching the review date convention). `endDate` is mutated by
 * the extend flow; `archivedAt` soft-deletes on replace/remove.
 */
export interface BridgeLetter {
  id: string
  reviewId: number
  startDate: string
  endDate: string
  storageKey: string
  fileName: string
  fileSize: number | null
  contentType: string
  uploadedById: string | null
  createdAt: string
  archivedAt: string | null
}

/**
 * Tracked subset of bridge-letter fields captured in edit history. Mirrors
 * `RiskSnapshot`: only the mutable, diff-meaningful fields. An extension diff is
 * just `{ field: 'endDate', old, new }`; `archivedAt` captures the archive transition.
 */
export type BridgeLetterSnapshot = Pick<BridgeLetter, 'startDate' | 'endDate' | 'archivedAt'>

export interface BridgeLetterEdit {
  id: string
  bridgeLetterId: string
  editorEmail: string
  editedAt: string
  previousData: BridgeLetterSnapshot | null
  newData: BridgeLetterSnapshot
  diff?: DiffEntry[] | null
}

export interface BridgeLetterHistoryResponse {
  edits: BridgeLetterEdit[]
}

export interface BridgeLetterResponse {
  bridgeLetter: BridgeLetter | null
}

// Trigger review types

export interface TriggerReviewRequest {
  sourceUrl: string
  reportType: ReportType
}

export interface TriggerReviewResponse {
  reviewId: number
  status: ReviewStatus
  vendorId: string
  vendorName: string
}

// Error response

export interface ApiErrorResponse {
  error: string
}

// Clients

export interface Client {
  id: string
  name: string
}

export interface ClientsListResponse {
  clients: Client[]
}

// Extended client types for Client Management page

export interface ClientListItem {
  id: string
  name: string
  contractStart: string | null // YYYY-MM-DD
  contractEnd: string | null // ISO timestamp
  keyExpiration: string | null // ISO timestamp
  keyStatus: 'valid' | 'invalid' | null // null until a sync has tested the key
  trustCenterEnabled: boolean
  hasKey: boolean
  createdAt: string
}

export interface ClientsDetailedListResponse {
  clients: ClientListItem[]
}

// Client CRUD request/response types

export interface CreateClientRequest {
  name: string
  contractStart: string // YYYY-MM-DD, required
  contractEnd?: string | null // ISO timestamp
  apiKey?: string | null // Optional password
  keyExpiration?: string | null // ISO timestamp
}

export interface UpdateClientDetailsRequest {
  name?: string
  contractStart?: string
  contractEnd?: string | null
  keyExpiration?: string | null
}

export interface UpdateClientKeyRequest {
  apiKey: string
  keyExpiration?: string | null
}

export interface ClientResponse {
  success: boolean
  client: ClientListItem
}

export interface RevokeKeyResponse {
  success: boolean
}

export interface DeleteClientResponse {
  success: boolean
}

// Document generation

export interface GenerateDocumentsRequest {
  clients: Array<{
    clientId?: string
    clientName: string
  }>
  uploadToDrata?: boolean
}

export interface DocumentGenerationResult {
  clientName: string
  status: 'success' | 'failed'
  documentId?: string
  googleDriveId?: string
  drataRequested?: boolean
  clientHasDrataKey?: boolean
  drataUploaded?: boolean
  drataError?: DrataUploadError
  error?: string
}

export interface GenerateDocumentsResponse {
  success: boolean
  results: DocumentGenerationResult[]
  summary: {
    total: number
    succeeded: number
    failed: number
  }
  /** Non-fatal problems (e.g. a bridge-letter co-upload failure) worth showing the user. */
  warnings?: string[]
}

// Document list (for DocumentsSection)

export type DocumentStatus = 'pending' | 'generated' | 'uploaded' | 'failed'

export interface ReviewDocument {
  id: string
  clientId: string
  clientName: string
  status: DocumentStatus
  googleDriveId: string | null
  createdAt: string
  updatedAt: string | null
  generatedBy: string | null
}

export interface ReviewDocumentsResponse {
  documents: ReviewDocument[]
}

// Retry Drata upload

export interface RetryDrataResponse {
  success: boolean
  status: 'uploaded' | 'failed'
  /** Why the push came back unsuccessful. Set whenever `success` is false. */
  error?: DrataUploadError
  /** Non-fatal problems worth showing the user (e.g. a partial Drata review left behind after a failed upload). */
  warning?: string
}

// ============================================================================
// User Management Types
// ============================================================================

/** User with timestamps and creator info for management UI */
export interface UserListItem extends User {
  createdAt: string
  updatedAt: string
  createdBy: { userId: string; email: string } | null
}

export interface UsersListResponse {
  users: UserListItem[]
}

export interface CreateUserRequest {
  email: string
  role: AssignableRole
}

export interface CreateUserResponse {
  user: UserListItem
}

export interface UpdateUserRoleRequest {
  role: AssignableRole
  reason?: string
}

export interface UpdateUserRoleResponse {
  success: true
  user: Pick<UserListItem, 'userId' | 'email' | 'role' | 'updatedAt'>
}

export interface RoleChangeItem {
  id: number
  previousRole: UserRole | null
  newRole: UserRole
  changedBy: { userId: string; email: string }
  changedAt: string
  reason: string | null
}

export interface RoleHistoryResponse {
  history: RoleChangeItem[]
}

export interface ForceLogoutResponse {
  success: true
  sessionsTerminated: number
}

// ============================================================================
// Vendor Management Types
// ============================================================================

// Vendor enums (text in DB, enforced in code)
export const VENDOR_RISK_LEVELS = ['None', 'Low', 'Moderate', 'High'] as const
export type VendorRiskLevel = (typeof VENDOR_RISK_LEVELS)[number]

export const VENDOR_BUSINESS_UNITS = [
  'Administrative',
  'Customer Success',
  'Engineering',
  'Finance',
  'Human Resources',
  'Information Technology',
  'Legal',
  'Marketing',
  'Product',
  'Sales',
  'Security',
] as const
export type VendorBusinessUnit = (typeof VENDOR_BUSINESS_UNITS)[number]

/** Vendor list item for the vendors table */
export interface VendorListItem {
  id: string
  name: string
  aliases: string[]
  website: string | null
  businessUnit: VendorBusinessUnit | null
  risk: VendorRiskLevel | null
  storesPii: boolean | null
  isSubprocessor: boolean | null
  reviewCount: number
  createdAt: string
  latestAuditPeriodEnd: string | null
  /**
   * Compact bridge-letter info for the latest review's rollup badge:
   * the active bridge letter's `endDate` (`YYYY-MM-DD`), or `null` when none.
   */
  bridgeLetterEndDate: string | null
  /** Whether that bridge letter is currently valid (`start <= today <= end`). */
  bridgeLetterValid: boolean
}

export interface VendorsListResponse {
  vendors: VendorListItem[]
}

/** Full vendor detail with all metadata */
export interface VendorDetail {
  id: string
  name: string
  aliases: string[]
  website: string | null
  trustCenterUrl: string | null
  privacyPolicyUrl: string | null
  tosUrl: string | null
  businessUnit: VendorBusinessUnit | null
  risk: VendorRiskLevel | null
  storesPii: boolean | null
  isSubprocessor: boolean | null
  createdAt: string
  archivedAt: string | null
  archivedBy: string | null
}

export interface VendorDetailResponse {
  vendor: VendorDetail
}

// Vendor CRUD request/response types

export interface CreateVendorRequest {
  name: string
  aliases?: string[]
  website?: string | null
  trustCenterUrl?: string | null
  privacyPolicyUrl?: string | null
  tosUrl?: string | null
  businessUnit?: VendorBusinessUnit | null
  risk?: VendorRiskLevel | null
  storesPii?: boolean | null
  isSubprocessor?: boolean | null
}

export interface UpdateVendorRequest {
  name?: string
  aliases?: string[]
  website?: string | null
  trustCenterUrl?: string | null
  privacyPolicyUrl?: string | null
  tosUrl?: string | null
  businessUnit?: VendorBusinessUnit | null
  risk?: VendorRiskLevel | null
  storesPii?: boolean | null
  isSubprocessor?: boolean | null
}

export interface VendorResponse {
  success: boolean
  vendor: VendorDetail
}

export interface ArchiveVendorResponse {
  success: boolean
  vendor: VendorDetail
}

/** Review list item shown on vendor detail page */
export interface VendorReviewListItem {
  id: number
  createdAt: string
  status: ReviewStatus
  reportType: string | null
  sourceUrl: string | null
  auditPeriodEnd: string | null
  createdBy: string | null
  approvedAt: string | null
  documentCount: number
  /** Active bridge letter attached to this review, if any. */
  bridgeLetter: BridgeLetter | null
  /**
   * True when no newer approved review exists for this vendor — gates the
   * "Bridge Letter until …" label. Populated for vendor-scoped lists
   * (getReviewsForVendor); omitted where "latest" is ill-defined (e.g. the
   * cross-vendor auditor list, which doesn't render the bridge label).
   */
  isLatestReview?: boolean
  /** Vendor info — only populated when the list is not already scoped to a single vendor (e.g. auditor detail page). */
  vendorId?: string
  vendorName?: string
}

export interface VendorReviewsListResponse {
  reviews: VendorReviewListItem[]
}

// ============================================================================
// Risk Management Types
// ============================================================================

export const RISK_TREATMENT_PLANS = ['Accept', 'Transfer', 'Avoid', 'Mitigate'] as const
export type RiskTreatmentPlan = (typeof RISK_TREATMENT_PLANS)[number]

/** Full risk record. */
export interface RiskDetail {
  id: string
  name: string
  description: string
  categories: string[]
  treatmentPlan: RiskTreatmentPlan
  treatmentDetails: string
  impact: number
  likelihood: number
  /** Computed: impact * likelihood. Not stored. */
  score: number
  residualImpact: number
  residualLikelihood: number
  /** Computed: residualImpact * residualLikelihood. Not stored. */
  residualScore: number
  controls: string[]
  applicable: boolean
  createdAt: string
  archivedAt: string | null
  archivedBy: string | null
}

/** Subset shown on the risks list page. */
export type RiskListItem = Pick<
  RiskDetail,
  | 'id'
  | 'name'
  | 'treatmentPlan'
  | 'impact'
  | 'likelihood'
  | 'score'
  | 'residualImpact'
  | 'residualLikelihood'
  | 'residualScore'
  | 'applicable'
  | 'createdAt'
>

export interface RisksListResponse {
  risks: RiskListItem[]
}

export interface RiskDetailResponse {
  risk: RiskDetail
}

export interface RiskResponse {
  success: boolean
  risk: RiskDetail
}

export interface ArchiveRiskResponse {
  success: boolean
  risk: RiskDetail
}

// Bulk upload risks to clients (request shape is `BulkUploadRisksToClientsInput` in @common/schemas)

export interface RiskClientResult {
  clientId: string | null
  clientName: string
  /**
   * - `created`: pushed successfully
   * - `skipped`: a risk with this name was already in the client's Drata (rendered as "Pre-existing")
   * - `failed`: Drata API call failed
   * - `no_drata_key`: client has no Drata API key configured (separate bucket — user-fixable)
   */
  status: 'created' | 'skipped' | 'failed' | 'no_drata_key'
  error?: string
}

export interface RiskUploadResult {
  riskId: string
  riskName: string
  clientResults: RiskClientResult[]
  summary: {
    total: number
    created: number
    skipped: number
    failed: number
    noDrataKey: number
  }
}

export interface BulkUploadRisksToClientsResponse {
  success: boolean
  results: RiskUploadResult[]
  overallSummary: {
    totalRisks: number
    totalClients: number
    created: number
    skipped: number
    failed: number
    noDrataKey: number
  }
}

/**
 * Tracked subset of risk fields captured in edit history. Excludes `id`,
 * `createdAt`, `archivedBy` (parent metadata) and the computed `score` /
 * `residualScore` (derivable from impact × likelihood). `archivedAt` is kept
 * so the archive transition produces a meaningful diff entry.
 */
export type RiskSnapshot = Pick<
  RiskDetail,
  | 'name'
  | 'description'
  | 'categories'
  | 'treatmentPlan'
  | 'treatmentDetails'
  | 'impact'
  | 'likelihood'
  | 'residualImpact'
  | 'residualLikelihood'
  | 'controls'
  | 'applicable'
  | 'archivedAt'
>

export interface RiskEdit {
  id: string
  riskId: string
  editorEmail: string
  editedAt: string
  previousData: RiskSnapshot | null
  newData: RiskSnapshot
  diff?: DiffEntry[] | null
}

export interface RiskHistoryResponse {
  edits: RiskEdit[]
}

// ============================================================================
// Auditor Management Types
// ============================================================================

export const AUDITOR_STATUSES = ['draft', 'published', 'archived'] as const
export type AuditorStatus = (typeof AUDITOR_STATUSES)[number]

/** Auditor list item for the auditors table */
export interface AuditorListItem {
  id: string
  name: string
  website: string | null
  opinion: string | null
  status: AuditorStatus
  createdAt: string
  lastPublishedAt: string | null
  reviewCount: number
}

/** Full auditor detail with all metadata */
export interface AuditorDetail extends AuditorListItem {
  createdBy: string | null
  lastPublishedBy: string | null
  updatedAt: string
  archivedAt: string | null
  archivedBy: string | null
}

export interface AuditorsListResponse {
  auditors: AuditorListItem[]
}

export interface AuditorDetailResponse {
  auditor: AuditorDetail
}

export interface CreateAuditorRequest {
  name: string
  website?: string | null
}

export interface UpdateAuditorRequest {
  name?: string
  website?: string | null
  opinion?: string | null
}

export interface AuditorResponse {
  success: boolean
  auditor: AuditorDetail
}

export interface PublishAuditorResponse {
  success: boolean
  auditor: AuditorDetail
}

export interface ArchiveAuditorResponse {
  success: boolean
  auditor: AuditorDetail
}

/** Tracked subset of auditor fields captured in edit history. */
export interface AuditorSnapshot {
  name: string
  website: string | null
  opinion: string | null
  status: AuditorStatus
}

export interface AuditorEdit {
  id: string
  auditorId: string
  editorEmail: string
  editedAt: string
  previousData: AuditorSnapshot | null
  newData: AuditorSnapshot
  diff?: DiffEntry[] | null
}

export interface AuditorHistoryResponse {
  edits: AuditorEdit[]
}

// ============================================================================
// Bulk upload to clients (multi-vendor)
// ============================================================================

export interface BulkUploadToClientsRequest {
  vendorIds: string[]
  clients: Array<{
    clientId?: string
    clientName: string
  }>
  uploadToDrata?: boolean
}

export interface VendorUploadResult {
  vendorId: string
  vendorName: string
  reviewId: number | null
  status: 'success' | 'failed' | 'skipped'
  error?: string
  clientResults: DocumentGenerationResult[]
  summary: {
    total: number
    succeeded: number
    failed: number
  }
}

export interface BulkUploadToClientsResponse {
  success: boolean
  results: VendorUploadResult[]
  overallSummary: {
    totalVendors: number
    vendorsSucceeded: number
    vendorsFailed: number
    vendorsSkipped: number
    totalDocuments: number
    documentsSucceeded: number
    documentsFailed: number
  }
}
